import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CheckoutEmbeddedForm } from '@barahpay/shared';

/**
 * `window[name] = value` with a name the server chose. The name is never evaluated as code, but it is
 * still refused unless it is a plain identifier that is not already a fixed property of `window`:
 * assigning over `location` or `top` would either throw or send the payer somewhere else entirely.
 */
function isAssignableGlobalName(name: string): boolean {
  if (!/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name)) {
    return false;
  }

  return Object.getOwnPropertyDescriptor(window, name)?.configurable !== false;
}

/**
 * Mounts a provider's own payment form inside this page, for providers that render their fields here
 * rather than taking the payer away.
 *
 * Everything about the form comes from the server and is treated as opaque: this component knows it is
 * loading *a* script and rendering *a* form with *some* attributes, and never which provider produced
 * them. That is what keeps a provider concept out of the checkout app (spec section 13).
 *
 * The payer's card details are entered in frames the provider serves, not in this page's DOM. Barah Pay
 * still never reads, holds or transmits a card number — but the page that hosts those frames is now in
 * PCI scope, which is why the integrity hash below is mandatory rather than best practice.
 */
export function ProviderWidgetMount({ form }: { form: CheckoutEmbeddedForm }) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  /*
   * A payment script with no subresource-integrity hash cannot be verified as the one the provider
   * published, which is the control PCI DSS 4.0 requirement 6.4.3 asks for. Refusing to mount is a
   * worse checkout and a safer one; degrading quietly is not an option a payment page gets.
   *
   * This is a fact about the props, not something that happens over time, so it is derived here rather
   * than pushed into state from the effect below.
   */
  const hasIntegrity = Boolean(form.scriptIntegrity);

  /*
   * A provider that names no global has nothing to set, and mounts normally. One that names an
   * unusable global is refused, because mounting the widget without the options it expects is the
   * worst outcome available: the payer gets a card form that dies silently at the 3-D Secure step.
   */
  const globalName = form.scriptOptionsName;
  const globalNameUsable = !globalName || isAssignableGlobalName(globalName);
  const blocked = !hasIntegrity || !globalNameUsable || loadFailed;

  useEffect(() => {
    if (!hasIntegrity || !globalNameUsable) {
      return;
    }

    const container = containerRef.current;

    if (!container) {
      return;
    }

    /*
     * First, and before the script element even exists: a provider script that configures itself from
     * a global reads it as it is evaluated, so one set afterwards is a global the script never saw.
     */
    const globals = window as unknown as Record<string, unknown>;
    const hadGlobal = globalName !== undefined && Object.prototype.hasOwnProperty.call(window, globalName);
    const previousGlobal = globalName === undefined ? undefined : globals[globalName];

    if (globalName !== undefined) {
      globals[globalName] = form.scriptOptionsValue;
    }

    /*
     * The provider's script looks for a form it recognises and replaces its contents. React must not
     * own those children — it would reconcile them away — so the form is created imperatively and
     * React only owns the container around it.
     */
    const paymentForm = document.createElement('form');
    paymentForm.action = form.formAction;
    paymentForm.method = 'post';

    if (form.formClassName) {
      paymentForm.className = form.formClassName;
    }

    for (const [name, value] of Object.entries(form.formData ?? {})) {
      paymentForm.setAttribute(`data-${name}`, value);
    }

    container.appendChild(paymentForm);

    const script = document.createElement('script');
    script.src = form.scriptUrl;
    script.integrity = form.scriptIntegrity!;
    script.crossOrigin = 'anonymous';
    script.async = true;

    /*
     * A blocked or failed script leaves an empty card with no explanation, which reads as a broken
     * page. The content-security policy is the most likely cause in a misconfigured environment, so
     * this says so rather than leaving the payer staring at nothing.
     */
    script.onerror = () => setLoadFailed(true);

    document.head.appendChild(script);

    return () => {
      /*
       * React 19 runs effects twice in StrictMode, and a payer who switches method re-runs this. Both
       * would otherwise leave a half-initialised widget behind the new one, so everything added here
       * is removed here — the script, and whatever it injected into the form.
       */
      script.remove();
      paymentForm.remove();
      container.replaceChildren();

      /*
       * The global is restored rather than merely deleted, so a re-mount starts from the window the
       * first mount found. What the provider's script itself left on `window` is out of reach, which
       * is why every mount builds a fresh script element rather than reusing one.
       */
      if (globalName === undefined) {
        return;
      }

      if (hadGlobal) {
        globals[globalName] = previousGlobal;
      } else {
        delete globals[globalName];
      }
    };
  }, [form, hasIntegrity, globalName, globalNameUsable]);

  if (blocked) {
    return (
      <div className="bp-notice bp-notice--error" role="alert">
        {t('checkout.widgetUnavailable')}
      </div>
    );
  }

  return (
    <>
      <div ref={containerRef} />

      <div className="bp-security">
        <span aria-hidden="true">🔒</span>
        <span>{t('checkout.cardDetailsNoticeEmbedded')}</span>
      </div>
    </>
  );
}
